import type {ReactNode} from 'react'
import {createContext, use, useMemo} from 'react'

import {useListResults} from '../hooks/use-list-results'
import type {LayoutPayload} from '../types'

interface ReposFinderStateContextValue {
  payload: LayoutPayload
  fetchedQuery: string
  currentPage: number
  queryParam: string
  locationSidebarItemId?: string
  /** Whether a location.state is present (soft-nav) or not (hard-nav) */
  hasLocationState: boolean
  fetchResults: (args: {q: string; page?: number; sidebarItemId?: string; focusHeaderAction?: boolean}) => void
}

const ReposFinderStateContext = createContext<ReposFinderStateContextValue | null>(null)

export function ReposFinderStateProvider({children, payload}: {children: ReactNode; payload: LayoutPayload}) {
  const {fetchedQuery, currentPage, fetchResults, queryParam, locationSidebarItemId, hasLocationState} = useListResults(
    {
      defaultFilterValue: 'contributed-by:@me',
    },
  )

  const value: ReposFinderStateContextValue = useMemo(
    () => ({
      payload,
      fetchedQuery,
      currentPage,
      fetchResults,
      queryParam,
      locationSidebarItemId,
      hasLocationState,
    }),
    [payload, fetchedQuery, currentPage, fetchResults, queryParam, locationSidebarItemId, hasLocationState],
  )

  return <ReposFinderStateContext value={value}>{children}</ReposFinderStateContext>
}

export function useReposFinderStateContext() {
  const context = use(ReposFinderStateContext)
  if (!context) {
    throw new Error('useReposFinderStateContext must be used within a ReposFinderStateProvider')
  }
  return context
}
